<template>
  <div class="dashboard-container">
    <profile-card></profile-card>
    <risk-status-card class="dashboard-risk-card mt-2" @click.native="routeToRiskStatus" />
    <daily-health-check-card class="dashboard-health-check-card mt-2" @click.native="routeToHealthStatus"/>
    <assignment-card class="dashboard-assignment-card mt-2" @click.native="routeToLearning"/>
    <!-- ListView -->
    <section class="alerts-container mt-3">
      <!-- Title -->
      <h5 class="alerts-title">{{ $t('dashboard.alertTitle') }}</h5>
      <!-- Search Field -->
      <div class="alerts-search-container">
        <div class="input-group">
          <font-awesome-icon :icon="['far', 'search']" class="search-icon my-auto ml-2"></font-awesome-icon>
          <input type="text" class="search-input form-control shadow-none"
                 :placeholder="$t('dashboard.searchPlaceholder')" v-model="searchTerm">
        </div>
      </div>
      <!-- List of Alerts -->
      <ul class="alert-listview-container mt-1" v-if="alerts">
        <li v-for="notification in alerts" :key="`alert-${notification.id}`" class="pl-3 pt-1 pb-1 listview-item d-flex"
            @click="openAlert(notification)">
          <div class="p-1" v-if="isNotificationUnread(notification)" style="width:20px;">
            <font-awesome-icon :icon="['fas', 'circle']" class="unread-icon"/>
          </div>
          <div class="p-1" v-else style="width:20px;">
            &nbsp;
          </div>
          <div class="p-1">
            <h5 class="list-view-title m-0 p-0">{{ getAlertTitle(notification) }}</h5>
            <p class="list-view-date">{{ formatDate(getRecordCreatedAt(notification)) }}</p>
            <div class="list-view-content" v-html="getAlertText(notification)"></div>
          </div>
        </li>
      </ul>
    </section>
    <!-- Modals -->
    <base-modal id="alert-modal">
      <template v-if="selectedNotification">
        <p class="alert-modal-static-title p-0 m-0">{{ $t('dashboard.alertModal.alert') }}</p>
        <h5 class="alert-modal-title p-0 m-0">{{ getAlertTitle(selectedNotification) }}</h5>
        <p class="alert-modal-date p-0 m-0">{{ formatDate(getRecordCreatedAt(selectedNotification)) }}</p>
        <hr/>
        <div class="alert-modal-body" v-html="getAlertText(selectedNotification)"></div>
      </template>
    </base-modal>
  </div>
</template>

<script>
import {debounce} from 'lodash';
import {format} from 'date-fns';
import {mapState, mapMutations, mapGetters} from 'vuex';
import common from "@/mixins/common";
import UserService from '@/services/user.service';
import NotificationService from '@/services/notification.service';

export default {
  name: 'DashboardPage',
  mixins: [common],
  components: {
    ProfileCard: () => import('@/components/profile-card/profile-card.vue'),
    RiskStatusCard: () => import('@/components/risk-status-card/risk-status-card.vue'),
    DailyHealthCheckCard: () => import('@/components/daily-health-check-card/daily-health-check-card.vue'),
    AssignmentCard: () => import('@/components/assignment-card/assignment-card.vue'),
    BaseModal: () => import('@/components/base-modal/base-modal.vue'),
  },
  data() {
    return {
      selectedNotification: null,
      searchTerm: '',
    };
  },
  created() {
    this.initialLoad();
  },
  computed: {
    ...mapState(['defaultOrganization', 'currentUser', 'alerts', 'unreadAlertCount']),
    ...mapGetters(['currentUserAlerts']),
  },
  methods: {
    ...mapMutations(['setUserAlerts', 'removeCurrentUserAlert', 'setAlerts', 'setUnreadAlertCount']),
    reset() {
      this.selectedNotification = null;
      this.searchTerm = '';
    },
    initialLoad() {
      this.reset();
      return this.getAlerts()
        .then(({data}) => {
          this.log('Dashboard -> initialLoad -> getAlerts', 'data', data);
          this.setAlerts(data);
          let unreadCount = 0;
          if (data) {
            for (let i = 0; i < data.length; i += 1) {
              const notification = data[i];
              if (notification.attributes.read === false) {
                unreadCount += 1;
              }
            }
          }
          this.setUnreadAlertCount(unreadCount);
          this.log('Dashboard -> initialLoad -> getAlerts', 'unreadCount', unreadCount);
        });
    },
    getAlertFromNotification(notification) {
      let alert = null;
      if (notification && notification.attributes && notification.attributes.notificable) {
        alert = notification.attributes.notificable;
      }
      return alert;
    },
    getAlertTitle(notification) {
      let title = "";
      const alert = this.getAlertFromNotification(notification);
      if (alert) {
        title = this.grabI18NTranslation(alert.alert_title);
      }
      return title;
    },
    getAlertText(notification) {
      let text = "";
      const alert = this.getAlertFromNotification(notification);
      if (alert) {
        text = this.grabI18NTranslation(alert.alert_text);
      }
      return text;
    },
    isNotificationUnread(notification) {
      let isUnread = false;
      if (notification && notification.attributes) {
        isUnread = !notification.attributes.read;
      }
      this.log('Dashboard -> isNotificationUnread', 'isUnread', isUnread);
      return isUnread;
    },
    formatDate(date) {
      return format(new Date(date), 'MM/dd/yy - hh:mm a');
    },
    getAlerts(extraParams = {}) {
      const params = {
        per_page: 50,
        ...extraParams,
      };
      this.log('Dashboard -> getAlerts', 'currentUser.id', this.currentUser.data.id);
      this.log('Dashboard -> getAlerts', 'defaultOrganization.id', this.defaultOrganization.data.id);

      return UserService.alertNotifications(this.currentUser.data.id, this.defaultOrganization.data.id, params);
    },
    alertInUserReadAlerts(alert) {
      const alertInUserReadAlert = this.currentUserAlerts.find((userAlert) => userAlert.id === alert.id);
      return alertInUserReadAlert !== null && alertInUserReadAlert !== undefined;
    },
    openAlert(notification) {
      NotificationService.markAsRead(notification.id).then(() => {
        return this.initialLoad();
      }).then(() => {
        this.selectedNotification = notification;
        this.openModal('alert-modal');
      });
    },
    routeToRiskStatus() {
      this.safelyNavigateToPath('RiskStatusPage');
    },
    routeToHealthStatus() {
      this.safelyNavigateToPath('HealthStatusPage');
    },
    routeToLearning() {
      this.safelyNavigateToPath('LearningPage');
    },
  },
  watch: {
    // eslint-disable-next-line
    searchTerm: debounce(function (newVal) {
      if (newVal.length > 1) {
        const params = {
          'query[subject_cont]': newVal,
        };
        this.getAlerts(params)
          .then(({data}) => {
            this.log('Dashboard -> search -> getAlerts', 'data', data);
            this.setAlerts(data);
          });
      } else if (newVal.length === 0) {
        this.initialLoad();
      }
    }, 250),
  },
};
</script>

<style lang="scss" scoped src="./styles.scss">
</style>
